/**
 * Harness: CoverDownload
 * 封面保存工具：把封面图统一转为可保存的 blob，
 * 再按运行环境选择保存通道，并提供移动端的多级兜底。
 *
 * 关键设计（华为/鸿蒙浏览器下载失败修复）：
 *   - jpg/png 等位图封面【不再经过 canvas】，直接 fetch 静态文件拿 blob——
 *     彻底绕开华为鸿蒙 webview 的 canvas.toBlob 生成损坏数据 / canvas tainted 问题；
 *     仅 svg 封面仍走 canvas 栅格化（且失败时报错，由调用方弹长按保存面板）。
 *   - a[download] + blob URL 这条链路在华为浏览器上会被拦截，因此触屏设备
 *     一律由调用方（PostDetail）直接弹「多方案保存面板」，不走本入口。
 *     本入口只保留给桌面浏览器的一键直接下载。
 *
 * 通道优先级（桌面直接下载时）：
 *   1. 系统 Web Share（files）—— iOS 分享面板可直接「存储图像」
 *   2. a[download] + blob URL —— 桌面 Chrome / Android Chrome
 *
 * 用法：
 *   const mode = await CoverDownload.download(url, '书名-A版-封面.jpg');
 *   mode: 'shared' | 'cancel' | 'webview-anchor' | 'anchor'
 *
 * 🔴 T136 (2026-11-03 02:00):shareBlob / shareUrlOnly 的返回值是**语义化档位**
 *   （'shared' | 'cancel' | 'unsupported' | 'failed'）,不再返回 boolean。
 *   ⇒ 'cancel' = 用户自己关掉了分享面板 ⇒ 调用方**必须静默**,不要报失败。
 */
const CoverDownload = (() => {
  /** 华为 / 鸿蒙环境（HUAWEI Browser、HarmonyOS、荣耀等）：a[download]+blob 链路不可靠 */
  const isHuawei = () =>
    // 🔴 T136:补 `Harmony`(不只 `HarmonyOS`):部分鸿蒙 UA 写作
    //   `HarmonyOS Next` / `Harmony/4.0` 变体,只匹配 `HarmonyOS` 会漏判
    //   ⇒ 面板仍显示「直接下载」⇒ 鸿蒙拦截 a[download] ⇒ 静默失败。
    /HuaweiBrowser|HUAWEI|Harmony|HONOR|honor|HWAPad/i.test(navigator.userAgent);

  /** 是否运行在会拦截下载的内置浏览器（微信 / 飞书 / 企业微信等） */
  const isWebView = () =>
    /MicroMessenger|WeChat|Feishu|Lark|LarkUA|wxwork|DingTalk|bytedance/i.test(navigator.userAgent);

  const isTouch = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /** 位图封面（jpg/png/webp）：直接 fetch 原文件，绕过 canvas */
  const isRasterUrl = (url) => /\.(jpe?g|png|webp)([?#]|$)/i.test(url);

  /**
   * 🔴 T114 (2026-10-27):判断一段字节是不是真图片。
   *
   * 为什么必须有这个判断 —— **CF Pages 对不存在的静态文件返回 200 + index.html**
   * (SPA 单页应用回退)。也就是说:
   *     fetch('/assets/covers/不存在的封面.jpg')
   *       → HTTP 200
   *       → Content-Type: text/html
   *       → body = 1055 字节的 <!DOCTYPE html> ...
   *
   * 而原来的 `fetchImageBlob` 只检查 `res.ok` ⇒ **把这段 HTML 存成 .jpg 下载给用户**。
   * 用户拿到的是一个打不开的"图片",而且他完全不知道出了什么事 ——
   * 这是最坏的失败模式(看起来像成功,实际是垃圾文件)。
   *
   * 判据同时看 Content-Type 和魔数,避免服务器把错误响应配成 image/jpeg。
   */
  const looksLikeImage = (bytes) => {
    if (!bytes || bytes.length < 3) return false;
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // PNG: 89 50 4E 47
    if (u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e) return true;
    // JPEG: FF D8 FF
    if (u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff) return true;
    // GIF: 47 49 46
    if (u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46) return true;
    // WebP: RIFF....WEBP
    if (
      u8.length >= 12 &&
      u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46 &&
      u8[8] === 0x57 && u8[9] === 0x45 && u8[10] === 0x42 && u8[11] === 0x50
    ) return true;
    return false;
  };

  const loadImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('cover image load failed: ' + url));
      img.src = url;
    });

  const fetchImageBlob = async (url, onProgress) => {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error('cover fetch failed: ' + res.status);

    const ctype = (res.headers.get('content-type') || '').toLowerCase();
    const total = Number(res.headers.get('content-length')) || 0;

    // 🔴 T114 (2026-10-27):SPA 回退 HTML 会在拿到字节后被拒。
    // 见上方 looksLikeImage 的完整说明 —— 这里只做「是不是图片」的判定,
    // 具体错误信息留给调用方区分「网络失败」和「拿到了网页而不是图片」。
    const assertImage = (bytes) => {
      if (looksLikeImage(bytes)) return true;
      if (ctype && !ctype.startsWith('image/') && ctype.indexOf('html') >= 0) {
        throw new Error('cover response was HTML (SPA fallback), not an image: ' + url);
      }
      throw new Error('cover response is not a valid image: ' + url);
    };

    // T-Cron-2026-10-23 08:00 — 改进清单 #5「进度条:下载封面进度」。
    //
    // 之前封面保存是**全有或全无**的:fetch 完之后 blob 一次性到手,
    // 期间 UI 只能显示「处理中…」。而封面是 200–600 KB 的 jpg,
    // 在 3G/弱网/华为浏览器代理链路上可能要 3–10 秒 —— 用户看不到任何进度,
    // 只能盯着一个不动的文字,很容易以为卡死而重复点击(触发多次并发下载)。
    //
    // 这里用 ReadableStream 按字节读,实时上报 {loaded, total}:
    //   · 有 content-length ⇒ total 已知 ⇒ 百分比进度条
    //   · 无 content-length / 老 webview 没有 res.body ⇒ total=0 ⇒ 不确定态进度条
    // 老浏览器没有 ReadableStream 时自动退回 res.blob()(行为与之前完全一致)。
    if (onProgress && res.body && typeof res.body.getReader === 'function') {
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      // 先把已知的 total 播报出去 —— 否则第一帧是「未知大小」的不确定态,
      // 紧接着跳到百分比,视觉上会闪一下。
      if (total > 0) onProgress({ loaded: 0, total });
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          received += value.length;
        }
        onProgress({ loaded: received, total });
      }
      const type = res.headers.get('content-type') || 'image/jpeg';
      const blob = new Blob(chunks, { type });
      if (!blob || blob.size === 0) throw new Error('cover fetch empty blob');
      // 🔴 拿到字节了,但可能是 SPA 回退的 HTML —— 必须验 magic bytes。
      assertImage(chunks.length ? chunks[0] : null);
      onProgress({ loaded: received, total: total || received });
      return blob;
    }

    if (onProgress) onProgress({ loaded: 0, total });
    const blob = await res.blob();
    if (!blob || blob.size === 0) throw new Error('cover fetch empty blob');
    // 🔴 同上:blob 非空 ≠ 是图片。SPA 回退 HTML 正好通过 size 检查。
    assertImage(await blob.slice(0, 16).arrayBuffer());
    if (onProgress) onProgress({ loaded: blob.size, total: total || blob.size });
    return blob;
  };

  /**
   * 封面图 → JPEG blob。
   * 位图：直接 fetch（华为浏览器上 canvas.toBlob 可能产出损坏 blob，必须绕开）。
   * svg：canvas 栅格化（白底填充）输出 jpg。
   */
  const toJpegBlob = async (url, onProgress) => {
    if (isRasterUrl(url)) {
      return await fetchImageBlob(url, onProgress);
    }
    const img = await loadImage(url);
    let w = img.naturalWidth;
    let h = img.naturalHeight;
    if (!w || !h) {
      // 个别 svg 无固有尺寸，按 3:4 规格兜底
      w = 1200;
      h = 1600;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('canvas toBlob failed'))),
        'image/jpeg',
        0.92
      );
    });
  };

  const canShareFiles = (blob) => {
    try {
      if (!navigator.share || typeof navigator.canShare !== 'function') return false;
      const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
      return navigator.canShare({ files: [file] });
    } catch (e) {
      return false;
    }
  };

  /** 触发 a[download]（blob URL） */
  const anchorDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.position = 'fixed';
    a.style.left = '-9999px';
    document.body.appendChild(a);
    a.click();
    // 留出时间窗，避免个别 webview 同步回收 blob URL
    setTimeout(() => {
      if (a.parentNode) a.parentNode.removeChild(a);
      URL.revokeObjectURL(url);
    }, 5000);
  };

  /**
   * 调起系统分享（含保存到相册/文件入口）。
   *
   * 🔴 T136 (2026-11-03 02:00):返回值从 boolean 改成**语义化档位**。
   *
   * 【为什么必须改】旧实现 `catch { return false }` 把三件事压成一坨:
   *   ① navigator.share 根本不存在(桌面 Chrome/Firefox)→ 应该「不支持」
   *   ② 用户自己关掉分享面板(AbortError)         → 应该**静默**
   *   ③ 真失败(权限/配额)                        → 应该「失败」
   * 调用方只看到 `false` ⇒ 统一弹「当前环境不支持系统分享」**并且自动打开
   * 下载面板** ⇒ 用户明明是自己按了取消,却被告知环境不支持,屏幕上还凭空
   * 冒出一个弹层挡住内容。
   *
   * 这正是 references/web-share-fallback-chain.md §Pitfalls #1 写的:
   *   "AbortError is success, not failure … Treating this as an error and
   *    showing '分享失败' toast punishes users for changing their mind."
   * ⇒ 代码此前与自家 reference 脱节,本轮对齐。
   *
   * 返回:
   *   'shared'     已调起系统分享(用户在面板里可以选「存储图像」)
   *   'cancel'     用户主动取消(AbortError)⇒ **调用方必须静默**
   *   'unsupported' 该环境没有 navigator.share
   *   'failed'     真失败(NotAllowedError / 其它)
   *
   * 🔴🔴 T149 (2026-11-05 08:00):把 `NotAllowedError` 从 'cancel' 挪到 'failed'。
   *
   * 【原实现的错在哪】T136 把 AbortError 和 NotAllowedError **一起**归为
   * 'cancel'(静默)。AbortError 确实是「用户自己关掉了分享面板」⇒ 静默正确。
   * 但 NotAllowedError 完全不同 —— 按 W3C Web Share 规范,它有**三个**成因:
   *   ① document 不允许使用 "web-share"(Permissions-Policy 未授权,
   *      例如 iframe 缺少 allow="web-share")  ⇒ **真失败**
   *   ② 调起时**没有 transient activation**(不在用户手势调用栈内)
   *      ⇒ 真失败(浏览器拒绝执行)
   *   ③ 文件类型因安全策略被拦截            ⇒ **真失败**
   * 三者都**不是**「用户改主意了」。
   *
   * 【为什么这在本站是可达的 —— 不是理论问题】
   * shareBlob 之前有 `await toJpegBlob(url, ...)`(一次 200~600KB 的网络
   * fetch)。fetch 之后再调 navigator.share,**用户手势的 transient activation
   * 可能已经过期** ⇒ 命中成因 ②。
   * 也就是说:用户点「系统分享保存」→ 封面下载完 → 系统分享面板**不弹** →
   * 屏幕上**什么都没发生**(0 个 toast、0 个弹层)。
   *
   * 【为什么静默在这里是最糟的形态】
   * 用户看到「什么都没发生」,无法区分:是自己按了取消?浏览器拦了?
   * 网络挂了?文件类型不让分享?
   * ⇒ 用户只能反复点击(点第二次又被 downloadingRef 防重入挡住,更困惑)。
   * 这与本项目 T-Cron 修过的 clipboard 静默失败 bug 是**完全同型**的缺陷:
   * 「点了没反应」是最难排查的失败形态。
   *
   * 【修法】'cancel' 只留给 AbortError(真取消);NotAllowedError → 'failed'
   * ⇒ 调用方已有的 `r === 'failed'` 分支会自动给出**可操作**的兜底提示
   *   (「分享未完成,请长按图片保存」/「请改用下载或复制链接」),
   *   两条路径(面板内按钮 / 快捷键 s)都不需要额外改动。
   */
  const shareBlob = async (blob, filename) => {
    // 🔴 守卫:navigator.share 在华为鸿蒙 WebView / 微信内置浏览器 /
    //   FB、IG 内置浏览器(Android WebView)里**根本不存在**
    //   ⇒ 直接调用会抛 TypeError: navigator.share is not a function。
    if (typeof navigator.share !== 'function') return 'unsupported';
    try {
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (e) {
      // 🔴 T149:'cancel' 只留给 AbortError —— 那是「用户自己关掉了分享面板」。
      //
      // NotAllowedError **不是**取消,按 W3C 规范它是三类真失败:
      //   ① Permissions-Policy 未授权 web-share
      //   ② 调起时没有 transient activation(不在用户手势调用栈内)
      //   ③ 文件类型被安全策略拦截
      // ⇒ 归为 'failed',让调用方给出可操作提示,而不是让用户面对「点了没反应」。
      if (e && e.name === 'AbortError') return 'cancel';
      return 'failed';
    }
  };

  /**
   * Level-2 降级:**纯文本**系统分享。
   *
   * 华为鸿蒙 WebView / 微信内置浏览器 / FB·IG 内置浏览器(T2 环境):
   *   navigator.share **存在**,但 canShare({files}) 返回 false。
   *   旧实现把它当成「不支持」⇒ 直接落到 a[download]
   *   ⇒ 这类环境静默拦截 a[download] ⇒ 用户点「保存封面」**什么都没发生**。
   *
   * 传 url + text 仍能调起系统分享面板,用户可在里面选「保存到相册」等目标。
   * ⇒ 这是 T2 环境里唯一真正能用的路径。
   */
  const shareUrlOnly = async (url, title, text) => {
    if (typeof navigator.share !== 'function') return 'unsupported';
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (e) {
      // 🔴 T149:与 shareBlob 保持同一口径 —— 只有 AbortError 是「用户取消」。
      // NotAllowedError(Permissions-Policy / 无 transient activation / 文件类型拦截)
      // 是真失败 ⇒ 走 'failed' 分支,调用方会给出「改用下载或复制链接」的兜底。
      if (e && e.name === 'AbortError') return 'cancel';
      return 'failed';
    }
  };

  /**
   * 桌面端主入口：尽力保存封面。
   * （触屏设备请勿使用本入口——华为/鸿蒙浏览器会拦截 a[download]，
   *  调用方应直接弹长按保存面板，见 PostDetail。）
   * 返回：
   *   'shared'        已调起系统分享（用户可在面板选择存储图像）
   *   'cancel'        用户取消了系统分享
   *   'webview-anchor' webview 内尝试了 a[download]，调用方应再给长按保存兜底
   *   'anchor'        常规浏览器已触发下载
   */
  const download = async (url, filename, onProgress) => {
    const blob = await toJpegBlob(url, onProgress);

    // 1) webview(微信/飞书等)优先系统分享——a[download] 在这类环境常被静默拦截
    //
    // 🔴 T136 (2026-11-03 02:00):补 Level-2 纯文本分享。
    //   旧实现 `canShareFiles(blob)` 为 false 就直接落到 anchorDownload
    //   ⇒ 但 T2 环境(华为鸿蒙 WV / 微信 / FB·IG 内置浏览器)
    //   **静默拦截 a[download]** ⇒ 用户点「保存封面」什么都没发生。
    //   ⇒ 现在 T2 走 shareUrlOnly(纯文本),那是这类环境唯一能用的路径。
    if (isWebView()) {
      // Level-1:文件分享(iOS 14+ / 新 Chrome / 部分华为 WV)
      if (canShareFiles(blob)) {
        const r = await shareBlob(blob, filename);
        if (r === 'shared') return 'shared';
        // 🔴 用户自己关掉了分享面板 ⇒ 静默返回 'cancel',
        //   **不要**再落到 anchorDownload(那会造成「假下载成功」提示)。
        if (r === 'cancel') return 'cancel';
        // r === 'failed' ⇒ 继续往下走 anchor 兜底
      } else {
        // Level-2:纯文本分享(华为 WV / 微信 / FB·IG 内置浏览器)
        const r2 = await shareUrlOnly(url, filename, `《${filename}》封面`);
        if (r2 === 'shared') return 'shared';
        if (r2 === 'cancel') return 'cancel';
        // 'unsupported' / 'failed' ⇒ 继续往下走 anchor 兜底
      }
    }

    // 2) 常规通道：a[download]
    anchorDownload(blob, filename);
    return isWebView() ? 'webview-anchor' : 'anchor';
  };

  /** 复制图片绝对链接（兜底手段） */
  const copyLink = async (url) => {
    const abs = new URL(url, window.location.href).href;
    try {
      await navigator.clipboard.writeText(abs);
      return true;
    } catch (e) {
      // 🔴🔴 T149 (2026-11-05 08:00):与 harness/clipboard.js 同型修复 ——
      //   removeChild 必须在 finally 里,否则 execCommand 抛异常时
      //   那个隐藏 textarea 会**永久留在 DOM 里**(每次失败留一份链接副本)。
      //   本函数是「复制图片链接」按钮的实现,失败路径同样可达。
      let ta = null;
      try {
        ta = document.createElement('textarea');
        ta.value = abs;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        return ok;
      } catch (e2) {
        return false;
      } finally {
        try { if (ta && ta.parentNode) ta.parentNode.removeChild(ta); } catch (_) { /* noop */ }
      }
    }
  };

  return {
    download,
    copyLink,
    isHuawei,
    isWebView,
    isTouch,
    canShareFiles,
    toJpegBlob, // 暴露给回归测试
    shareBlob,
    shareUrlOnly, // 🔴 T136:Level-2 纯文本分享(T2 环境唯一可用路径)
    looksLikeImage, // 暴露给 UI 层做封面加载失败的降级判断
  };
})();

window.CoverDownload = CoverDownload;

export default CoverDownload;
